/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { CdsDisclosure, type Props$1 as CdsDisclosureProps } from '@coursera-web/cds-labs';

import { Skeleton } from '@coursera/cds-core';

import { CoachBrandLabel, CoachBrandLabelWithBeta } from 'bundles/ai-coach-platform/components/branding/CoachBranding';

import _t from 'i18n!nls/ai-coach-platform';

import ShowMoreDisclosureButtons from './ShowMoreDisclosureButtons';

const DisclosureSkeleton = () => {
  return (
    <div className="coach-disclosure-skeleton">
      <Skeleton variant="text" height={24} />
      <Skeleton variant="text" width="50%" height={24} />

      <div
        css={css`
          display: flex;
          gap: 8px;
          margin: var(--cds-spacing-200) 0;
        `}
      >
        <Skeleton
          variant="rect"
          width={120}
          height={36}
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
        <Skeleton
          variant="rect"
          width={120}
          height={36}
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
      </div>
    </div>
  );
};

export type Props = Omit<CdsDisclosureProps, 'header' | 'aria-label'> & {
  'aria-label'?: string;

  /**
   * list of buttons to render in the Disclosure footer area
   */
  actionButtons?: React.ReactNode[];

  /**
   * Shows "Beta" next to the Coach header logo
   */
  showBetaTag?: boolean;
  /**
   * Shows a skeleton version of the disclosure
   * @default false
   */
  loading?: boolean;

  additionalStyles?: SerializedStyles;
};

/**
 * Simple wrapper around CDS Disclosure for Coach-specific use cases.
 */
export const CoachDisclosure = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const {
    loading = false,
    showBetaTag = false,
    actionButtons,
    'aria-label': ariaLabel = _t('#{coachLabel}, AI powered assistant, beta', { coachLabel: 'Coach' }),
    children,
    onToggle,
    additionalStyles,
    ...rest
  } = props;

  const showFooter = loading ? false : !!actionButtons;

  return (
    <CdsDisclosure
      {...rest}
      ref={ref}
      aria-label={ariaLabel}
      header={
        <div css={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showBetaTag ? <CoachBrandLabelWithBeta /> : <CoachBrandLabel />}
        </div>
      }
      footer={
        showFooter && !!actionButtons ? <ShowMoreDisclosureButtons>{actionButtons}</ShowMoreDisclosureButtons> : null
      }
      onToggle={onToggle}
      css={additionalStyles}
    >
      {loading && <DisclosureSkeleton />}
      {!loading && children}
    </CdsDisclosure>
  );
});

export default CoachDisclosure;
